import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../App';
import { complaintsApi } from '../services/formsApi';
import { getQuestionLabel, resolveAnswer, findField, findRoutingField } from '../utils/formLabels';
import {
  CheckCircle,
  Edit,
  Save,
  X,
  Clock,
  User,
  FileText,
  AlertCircle,
  Search,
  Filter,
  Calendar,
  Building,
  Trash2,
  Sparkles,
  Loader2,
  ShieldAlert
} from 'lucide-react';
import { displayProtocolTag } from '../utils/protocol';

const SEVERITY_ORDER = { crítico: 0, critico: 0, alto: 1, médio: 2, medio: 2, baixo: 3 };

// Públicos canônicos do sistema (os mesmos de middleware/adminLevels.js). Usados
// quando o formulário do cliente não configura o campo sobre_quem: preencher à
// mão com texto livre gravaria um valor que o filtro de níveis não reconhece,
// escondendo a denúncia de client-admins com públicos atribuídos.
const PUBLICOS_PADRAO = [
  { value: 'diretores', label: 'Diretores' },
  { value: 'presidencia', label: 'Presidência' },
  { value: 'demais_colaboradores', label: 'Demais colaboradores' }
];

const getSeverityStyle = (severity) => {
  const value = (severity || '').toLowerCase();
  if (value.includes('crít') || value.includes('crit')) {
    return 'bg-red-100 text-red-800 border-red-200';
  }
  if (value === 'alto') {
    return 'bg-orange-100 text-orange-800 border-orange-200';
  }
  if (value.includes('méd') || value.includes('med')) {
    return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  }
  return 'bg-green-100 text-green-800 border-green-200';
};

const getQualityStyle = (qualidade) => {
  if (qualidade === 'provavel_teste') {
    return 'bg-gray-200 text-gray-800 border-gray-300';
  }
  if (qualidade === 'insuficiente') {
    return 'bg-amber-100 text-amber-900 border-amber-300';
  }
  return 'bg-emerald-100 text-emerald-800 border-emerald-200';
};

const QUALITY_LABELS = {
  provavel_teste: 'Provável teste',
  insuficiente: 'Relato insuficiente',
  suficiente: 'Conteúdo substantivo'
};

const RECOMMENDATION_LABELS = {
  arquivar_provavel_teste: 'Arquivar — provável teste do canal',
  solicitar_complementacao: 'Solicitar complementação ao denunciante',
  investigar: 'Prosseguir com investigação',
  aguardar_dados: 'Aguardar mais dados'
};

const ESCOPO_LABELS = {
  dentro_do_escopo: 'Dentro do escopo',
  fora_do_escopo: 'Fora do escopo'
};

const NORMA_BASE_LABELS = {
  norma_interna: 'Norma interna',
  legislacao: 'Legislação'
};

const getEscopoStyle = (escopo) => (
  escopo === 'fora_do_escopo'
    ? 'bg-red-50 text-red-800 border-red-200'
    : escopo === 'dentro_do_escopo'
      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
      : 'bg-gray-100 text-gray-600 border-gray-200'
);

const TriageInsight = ({ complaint, onReanalyze, reanalyzing }) => {
  const phase = complaint.ai_phase || 'none';
  const plan = complaint.ai_analise;
  const structured = plan?.denuncia_estruturada;
  const actionPlan = plan?.plano_acao;
  const grounding = plan?.metadata?.grounding;
  const qualidade = structured?.qualidade_conteudo;
  const recomendacao = structured?.recomendacao_aprovador || actionPlan?.acao_recomendada;

  if (phase === 'investigation_pending' || phase === 'none') {
    return (
      <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-center gap-2 text-blue-800">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm font-medium">Triagem automática em andamento...</span>
        </div>
        <p className="mt-2 text-sm text-blue-700">
          O agente de IA está classificando a denúncia e montando o plano preliminar.
        </p>
      </div>
    );
  }

  if (phase === 'failed') {
    return (
      <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="flex items-center gap-2 text-red-800">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm font-medium">Triagem automática falhou</span>
        </div>
        <p className="mt-2 text-sm text-red-700">
          Atualize a página para tentar novamente ou revise a denúncia manualmente.
        </p>
      </div>
    );
  }

  if (!structured) {
    return null;
  }

  return (
    <div className={`mt-4 rounded-lg border p-4 ${
      qualidade === 'provavel_teste'
        ? 'border-gray-300 bg-gray-50'
        : qualidade === 'insuficiente'
          ? 'border-amber-300 bg-amber-50/40'
          : 'border-indigo-200 bg-indigo-50/50'
    }`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Sparkles className="h-5 w-5 text-indigo-600" />
        <h4 className="text-sm font-semibold text-indigo-900">Triagem IA — Agente 1</h4>
        {qualidade ? (
          <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${getQualityStyle(qualidade)}`}>
            {QUALITY_LABELS[qualidade] || qualidade}
          </span>
        ) : null}
        <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${getSeverityStyle(structured.severidade)}`}>
          Severidade: {structured.severidade || 'N/A'}
        </span>
        <span className="rounded-full border border-indigo-200 bg-white px-2 py-0.5 text-xs font-medium text-indigo-800">
          Urgência: {structured.urgencia || 'N/A'}
        </span>
        {structured.confianca_classificacao ? (
          <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-600">
            Confiança: {structured.confianca_classificacao}
          </span>
        ) : null}
        {grounding === 'compliance_geral' ? (
          <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-600">
            Compliance geral (sem RAG)
          </span>
        ) : grounding === 'rag' ? (
          <span className="rounded-full border border-green-200 bg-white px-2 py-0.5 text-xs text-green-700">
            Políticas internas (RAG)
          </span>
        ) : null}
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <p className="text-xs font-medium uppercase text-gray-500">Natureza</p>
          <p className="text-sm font-medium text-gray-900">{structured.natureza || 'Não classificada'}</p>
        </div>
        <div>
          <p className="text-xs font-medium uppercase text-gray-500">Título</p>
          <p className="text-sm font-medium text-gray-900">{structured.titulo || 'Sem título'}</p>
        </div>
      </div>

      {structured.resumo_executivo ? (
        <div className="mt-3 rounded-md bg-white p-3">
          <p className="text-xs font-medium uppercase text-gray-500">Resumo executivo</p>
          <p className="mt-1 text-sm text-gray-800">{structured.resumo_executivo}</p>
        </div>
      ) : null}

      {recomendacao ? (
        <div className={`mt-3 rounded-md border p-3 text-sm ${
          qualidade === 'provavel_teste'
            ? 'border-gray-300 bg-white text-gray-800'
            : 'border-indigo-200 bg-white text-indigo-900'
        }`}>
          <p className="text-xs font-medium uppercase text-gray-500">Recomendação ao aprovador</p>
          <p className="mt-1 font-medium">
            {RECOMMENDATION_LABELS[recomendacao] || recomendacao}
          </p>
        </div>
      ) : null}

      {structured.sinais_detectados?.length > 0 ? (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-medium uppercase text-amber-800">Sinais detectados</p>
          <ul className="mt-1 space-y-1 text-sm text-amber-900">
            {structured.sinais_detectados.map((sinal, index) => (
              <li key={index}>• {sinal}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {structured.lacunas_informacao?.length > 0 ? (
        <div className="mt-3">
          <p className="text-xs font-medium uppercase text-gray-500">Lacunas de informação</p>
          <ul className="mt-1 space-y-1 text-sm text-gray-700">
            {structured.lacunas_informacao.slice(0, 5).map((lacuna, index) => (
              <li key={index}>• {lacuna}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {actionPlan?.perguntas_investigativas?.length > 0 ? (
        <div className="mt-3">
          <p className="text-xs font-medium uppercase text-gray-500">Questões investigativas</p>
          <ul className="mt-1 space-y-1 text-sm text-gray-700">
            {actionPlan.perguntas_investigativas.map((question, index) => (
              <li key={index} className="flex gap-2">
                <span className="text-indigo-500">•</span>
                <span>{question}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {actionPlan?.entrevistas_planejadas?.length > 0 ? (
        <div className="mt-3">
          <p className="text-xs font-medium uppercase text-gray-500">Entrevistas planejadas</p>
          <ul className="mt-1 space-y-1 text-sm text-gray-700">
            {actionPlan.entrevistas_planejadas.map((entrevista, index) => {
              const pessoa = typeof entrevista === 'object' && entrevista !== null
                ? (entrevista.pessoa || entrevista.nome || '')
                : String(entrevista);
              const objetivo = typeof entrevista === 'object' && entrevista !== null
                ? (entrevista.objetivo || '')
                : '';
              return (
                <li key={index} className="flex gap-2">
                  <span className="text-indigo-500">•</span>
                  <span>
                    <span className="font-medium">{pessoa}</span>
                    {objetivo ? ` — ${objetivo}` : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {actionPlan?.documentos_necessarios?.length > 0 ? (
        <div className="mt-3">
          <p className="text-xs font-medium uppercase text-gray-500">Documentos e evidências a coletar</p>
          <ul className="mt-1 space-y-1 text-sm text-gray-700">
            {actionPlan.documentos_necessarios.map((doc, index) => (
              <li key={index} className="flex gap-2">
                <span className="text-indigo-500">•</span>
                <span>{typeof doc === 'object' ? JSON.stringify(doc) : doc}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {actionPlan?.cronograma_sugerido ? (
        <div className="mt-3">
          <p className="text-xs font-medium uppercase text-gray-500">Cronograma sugerido</p>
          <p className="mt-1 text-sm text-gray-700">{actionPlan.cronograma_sugerido}</p>
        </div>
      ) : null}

      {actionPlan?.notas_investigador ? (
        <div className="mt-3 flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{actionPlan.notas_investigador}</p>
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500">
          Triagem automática concluída. A aprovação da denúncia continua sendo decisão humana.
        </p>
        {onReanalyze ? (
          <button
            type="button"
            onClick={() => onReanalyze(complaint.id)}
            disabled={reanalyzing === complaint.id}
            className="rounded-md border border-indigo-300 bg-white px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
          >
            {reanalyzing === complaint.id ? 'Reanalisando...' : 'Reanalisar com IA'}
          </button>
        ) : null}
      </div>
    </div>
  );
};

/**
 * Análise do Caso — o material que o advogado pleno preparava à mão, agora
 * pré-preenchido pela IA e totalmente revisável pelo aprovador antes da
 * liberação: escopo, resumo, normas supostamente violadas e plano de ação.
 * Os valores aprovados são gravados nos campos oficiais da denúncia.
 */
const CaseAnalysisSection = ({
  complaint,
  isEditing,
  data,
  onFieldChange,
  onNormaChange,
  onNormaAdd,
  onNormaRemove
}) => {
  const current = (field, fallback = '') => {
    if (isEditing && data[field] !== undefined) return data[field];
    return complaint[field] ?? fallback;
  };

  const escopo = current('escopo', '');
  const escopoJustificativa = current('escopo_justificativa', '');
  const resumo = current('resumo_denuncia', '');
  const plano = current('plano_investigacao', '');
  const normas = isEditing
    ? (data.normas_violadas || [])
    : (Array.isArray(complaint.normas_violadas) ? complaint.normas_violadas : []);

  const isEmpty = !escopo && !resumo && !plano && normas.length === 0;

  return (
    <div className="mt-4 rounded-lg border border-slate-300 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <FileText className="h-5 w-5 text-slate-700" />
        <h4 className="text-sm font-semibold text-slate-900">Análise do Caso</h4>
        {escopo ? (
          <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${getEscopoStyle(escopo)}`}>
            {ESCOPO_LABELS[escopo] || escopo}
          </span>
        ) : null}
        <span className="text-xs text-gray-500">
          Pré-preenchida pela IA — revise e ajuste antes de aprovar. Estes campos são gravados na denúncia liberada.
        </span>
      </div>

      {isEmpty && !isEditing ? (
        <p className="text-sm text-gray-500">
          Análise ainda não disponível. Aguarde a triagem automática ou clique em Editar para preencher manualmente.
        </p>
      ) : null}

      {/* Escopo */}
      <div className="mb-3">
        <p className="text-xs font-medium uppercase text-gray-500">Classificação de escopo</p>
        {isEditing ? (
          <div className="mt-1 space-y-2">
            <select
              value={escopo || ''}
              onChange={(e) => onFieldChange('escopo', e.target.value)}
              className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Não classificado</option>
              <option value="dentro_do_escopo">Dentro do escopo</option>
              <option value="fora_do_escopo">Fora do escopo</option>
            </select>
            <textarea
              value={escopoJustificativa || ''}
              onChange={(e) => onFieldChange('escopo_justificativa', e.target.value)}
              placeholder="Justificativa da classificação de escopo"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
        ) : (
          escopoJustificativa ? (
            <p className="mt-1 text-sm text-gray-800">{escopoJustificativa}</p>
          ) : (
            !escopo ? <p className="mt-1 text-sm text-gray-400 italic">Não classificado</p> : null
          )
        )}
      </div>

      {/* Resumo do caso */}
      <div className="mb-3">
        <p className="text-xs font-medium uppercase text-gray-500">Resumo do caso</p>
        {isEditing ? (
          <textarea
            value={resumo || ''}
            onChange={(e) => onFieldChange('resumo_denuncia', e.target.value)}
            placeholder="Resumo formal do caso, pronto para o registro oficial"
            rows={5}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
        ) : (
          <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">
            {resumo || <span className="text-gray-400 italic">Não preenchido</span>}
          </p>
        )}
      </div>

      {/* Normas supostamente violadas */}
      <div className="mb-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium uppercase text-gray-500">Normas supostamente violadas</p>
          {isEditing ? (
            <button
              type="button"
              onClick={onNormaAdd}
              className="rounded-md border border-blue-300 bg-white px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
            >
              + Adicionar norma
            </button>
          ) : null}
        </div>
        {normas.length === 0 && !isEditing ? (
          <p className="mt-1 text-sm text-gray-400 italic">Nenhuma norma indicada</p>
        ) : null}
        <div className="mt-1 space-y-2">
          {normas.map((norma, index) => (
            isEditing ? (
              <div key={index} className="rounded-md border border-gray-200 bg-gray-50 p-2">
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <input
                    type="text"
                    value={norma.documento || ''}
                    onChange={(e) => onNormaChange(index, 'documento', e.target.value)}
                    placeholder="Documento/lei (ex.: Código de Conduta)"
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    value={norma.dispositivo || ''}
                    onChange={(e) => onNormaChange(index, 'dispositivo', e.target.value)}
                    placeholder="Item/artigo (opcional)"
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="text"
                    value={norma.descricao || ''}
                    onChange={(e) => onNormaChange(index, 'descricao', e.target.value)}
                    placeholder="Por que a conduta relatada violaria esta norma"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={norma.base || 'norma_interna'}
                    onChange={(e) => onNormaChange(index, 'base', e.target.value)}
                    className="px-2 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="norma_interna">Norma interna</option>
                    <option value="legislacao">Legislação</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => onNormaRemove(index)}
                    className="rounded-md border border-red-200 bg-white p-2 text-red-600 hover:bg-red-50"
                    aria-label="Remover norma"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div key={index} className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 p-2 text-sm">
                <span className={`mt-0.5 shrink-0 rounded-full border px-2 py-0.5 text-xs ${
                  norma.base === 'legislacao'
                    ? 'border-blue-200 bg-blue-50 text-blue-800'
                    : 'border-purple-200 bg-purple-50 text-purple-800'
                }`}>
                  {NORMA_BASE_LABELS[norma.base] || 'Norma'}
                </span>
                <div>
                  <p className="font-medium text-gray-900">
                    {norma.documento || 'Documento não informado'}
                    {norma.dispositivo ? <span className="font-normal text-gray-600"> — {norma.dispositivo}</span> : null}
                  </p>
                  {norma.descricao ? <p className="text-gray-700">{norma.descricao}</p> : null}
                </div>
              </div>
            )
          ))}
        </div>
      </div>

      {/* Plano de ação para a apuração */}
      <div>
        <p className="text-xs font-medium uppercase text-gray-500">Plano de ação para a apuração</p>
        {isEditing ? (
          <textarea
            value={plano || ''}
            onChange={(e) => onFieldChange('plano_investigacao', e.target.value)}
            placeholder="Plano de investigação recomendado (entrevistas, documentos, questões a esclarecer, cronograma)"
            rows={8}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono"
          />
        ) : (
          <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">
            {plano || <span className="text-gray-400 italic">Não preenchido</span>}
          </p>
        )}
      </div>
    </div>
  );
};

const ComplaintApproval = () => {
  const { user } = useAuth();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [deleteModal, setDeleteModal] = useState({ open: false, complaintId: null, confirmationText: '' });
  const [deleting, setDeleting] = useState(null);
  const [reanalyzing, setReanalyzing] = useState(null);

  useEffect(() => {
    loadPendingApprovals();
  }, []);

  useEffect(() => {
    const hasPendingTriage = complaints.some(
      (c) => c.ai_phase === 'investigation_pending' || c.ai_phase === 'none'
    );

    if (!hasPendingTriage) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      loadPendingApprovals({ silent: true });
    }, 3000);

    return () => window.clearInterval(interval);
  }, [complaints]);

  const loadPendingApprovals = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await complaintsApi.getPendingApprovals({ retryFailed: !silent });
      if (response.success) {
        setComplaints(response.complaints || []);
      } else {
        toast.error('Erro ao carregar denúncias pendentes');
      }
    } catch (error) {
      console.error('Erro ao carregar denúncias pendentes:', error);
      toast.error('Erro ao carregar denúncias pendentes');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const startEditing = (complaint) => {
    setEditingId(complaint.id);
    setEditedData({
      sobre_quem: complaint.sobre_quem || '',
      responses: complaint.responses || {},
      escopo: complaint.escopo || '',
      escopo_justificativa: complaint.escopo_justificativa || '',
      resumo_denuncia: complaint.resumo_denuncia || '',
      normas_violadas: Array.isArray(complaint.normas_violadas)
        ? complaint.normas_violadas.map((n) => ({ ...n }))
        : [],
      plano_investigacao: complaint.plano_investigacao || ''
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditedData({});
  };

  const handleFieldChange = (field, value) => {
    console.log('🔄 handleFieldChange chamado:', { field, value });
    setEditedData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      console.log('📝 Novo editedData:', newData);
      return newData;
    });
  };

  const handleResponseFieldChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      responses: {
        ...prev.responses,
        [field]: value
      }
    }));
  };

  const handleNormaChange = (index, field, value) => {
    setEditedData(prev => {
      const normas = [...(prev.normas_violadas || [])];
      normas[index] = { ...normas[index], [field]: value };
      return { ...prev, normas_violadas: normas };
    });
  };

  const handleNormaAdd = () => {
    setEditedData(prev => ({
      ...prev,
      normas_violadas: [
        ...(prev.normas_violadas || []),
        { documento: '', dispositivo: '', descricao: '', base: 'norma_interna' }
      ]
    }));
  };

  const handleNormaRemove = (index) => {
    setEditedData(prev => ({
      ...prev,
      normas_violadas: (prev.normas_violadas || []).filter((_, i) => i !== index)
    }));
  };

  const saveChanges = async (complaintId) => {
    try {
      setSaving(true);
      const updateData = {};

      if (editedData.sobre_quem !== undefined) {
        updateData.sobre_quem = editedData.sobre_quem;
      }

      if (editedData.responses !== undefined) {
        updateData.responses = editedData.responses;
      }

      // Campos da análise do caso (escopo, resumo, normas, plano)
      ['escopo', 'escopo_justificativa', 'resumo_denuncia', 'plano_investigacao'].forEach((field) => {
        if (editedData[field] !== undefined) {
          updateData[field] = editedData[field];
        }
      });
      if (editedData.normas_violadas !== undefined) {
        updateData.normas_violadas = (editedData.normas_violadas || []).filter(
          (n) => (n.documento || '').trim() !== '' || (n.descricao || '').trim() !== ''
        );
      }

      await complaintsApi.updatePendingComplaint(complaintId, updateData);
      toast.success('Alterações salvas com sucesso!');
      setEditingId(null);
      setEditedData({});
      loadPendingApprovals();
    } catch (error) {
      console.error('Erro ao salvar alterações:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar alterações');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (complaintId) => {
    setDeleteModal({ open: true, complaintId, confirmationText: '' });
  };

  const handleDeleteConfirm = async () => {
    const requiredText = 'tenho certeza que vou deletar a denúncia';
    
    if (deleteModal.confirmationText.toLowerCase().trim() !== requiredText.toLowerCase().trim()) {
      toast.error('Texto de confirmação incorreto. Digite exatamente: "tenho certeza que vou deletar a denúncia"');
      return;
    }
    
    try {
      setDeleting(deleteModal.complaintId);
      await complaintsApi.deleteComplaint(deleteModal.complaintId);
      toast.success('Denúncia deletada com sucesso');
      setDeleteModal({ open: false, complaintId: null, confirmationText: '' });
      loadPendingApprovals();
    } catch (error) {
      console.error('Erro ao deletar denúncia:', error);
      toast.error(error.response?.data?.error || 'Erro ao deletar denúncia');
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, complaintId: null, confirmationText: '' });
  };

  const handleReanalyze = async (complaintId) => {
    try {
      setReanalyzing(complaintId);
      await complaintsApi.reanalyzeTriage(complaintId);
      toast.info('Reanálise de triagem iniciada.');
      await loadPendingApprovals({ silent: true });
    } catch (error) {
      console.error('Erro ao reanalisar triagem:', error);
      toast.error(error.response?.data?.error || 'Erro ao reanalisar triagem');
    } finally {
      setReanalyzing(null);
    }
  };

  const approveComplaint = async (complaintId) => {
    if (!window.confirm('Tem certeza que deseja aprovar esta denúncia? Após a aprovação, os e-mails serão enviados e a denúncia aparecerá nos dashboards.')) {
      return;
    }

    try {
      setApproving(complaintId);
      await complaintsApi.approveComplaint(complaintId);
      toast.success('Denúncia aprovada com sucesso!');
      loadPendingApprovals();
    } catch (error) {
      console.error('Erro ao aprovar denúncia:', error);
      toast.error(error.response?.data?.error || 'Erro ao aprovar denúncia');
    } finally {
      setApproving(null);
    }
  };

  // Filtrar denúncias
  const filteredComplaints = complaints
    .filter(complaint => {
    const matchesSearch = searchTerm === '' || 
      complaint.tracking_token?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      complaint.sobre_quem?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      JSON.stringify(complaint.responses || {}).toLowerCase().includes(searchTerm.toLowerCase()) ||
      complaint.ai_analise?.denuncia_estruturada?.titulo?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesClient = clientFilter === 'all' || complaint.client_slug === clientFilter;

    const aiSeverity = (complaint.ai_analise?.denuncia_estruturada?.severidade || '').toLowerCase();
    const matchesSeverity = severityFilter === 'all' || aiSeverity.includes(severityFilter);
    
    return matchesSearch && matchesClient && matchesSeverity;
  })
    .sort((a, b) => {
      const sevA = (a.ai_analise?.denuncia_estruturada?.severidade || 'baixo').toLowerCase();
      const sevB = (b.ai_analise?.denuncia_estruturada?.severidade || 'baixo').toLowerCase();
      const orderA = SEVERITY_ORDER[sevA] ?? 4;
      const orderB = SEVERITY_ORDER[sevB] ?? 4;
      if (orderA !== orderB) return orderA - orderB;
      return new Date(b.submitted_at) - new Date(a.submitted_at);
    });

  // Obter lista de clientes únicos
  const uniqueClients = [...new Set(complaints.map(c => c.client_slug).filter(Boolean))];

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const renderField = (label, value, isEditing, onChange, options = null) => {
    if (isEditing) {
      // Se tem opções, usar select
      if (options && options.length > 0) {
        // Preparar opções normalizadas
        const normalizedOptions = options.map((option) => {
          if (typeof option === 'object' && option !== null) {
            return {
              value: String(option.value || option.label || ''),
              label: String(option.label || option.value || '')
            };
          }
          return {
            value: String(option),
            label: String(option)
          };
        });
        
        // Encontrar valor correspondente
        const currentValueStr = value ? String(value).trim() : '';
        let selectedValue = '';
        
        // Primeiro, tentar encontrar pelo value exato
        const exactMatch = normalizedOptions.find(opt => String(opt.value).trim() === currentValueStr);
        if (exactMatch) {
          selectedValue = exactMatch.value;
        } else {
          // Tentar encontrar pelo label
          const labelMatch = normalizedOptions.find(opt => String(opt.label).trim() === currentValueStr);
          if (labelMatch) {
            selectedValue = labelMatch.value;
          } else if (currentValueStr) {
            // Se não encontrou, usar o valor original
            selectedValue = currentValueStr;
          }
        }
        
        return (
          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <select
              key={`select-${label}-${selectedValue}`}
              value={selectedValue || ''}
              onChange={(e) => {
                const newValue = String(e.target.value || '');
                console.log('🔄 Select onChange disparado!', { 
                  newValue, 
                  eventType: e.type,
                  targetValue: e.target.value,
                  selectedIndex: e.target.selectedIndex,
                  options: normalizedOptions.length
                });
                if (onChange && typeof onChange === 'function') {
                  console.log('✅ Chamando onChange com:', newValue);
                  onChange(newValue);
                } else {
                  console.error('❌ onChange não é uma função!', onChange);
                }
              }}
              onClick={(e) => {
                console.log('🖱️ Select onClick disparado!', { 
                  value: e.target.value,
                  selectedIndex: e.target.selectedIndex 
                });
              }}
              onMouseDown={(e) => {
                console.log('🖱️ Select onMouseDown disparado!');
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Selecione uma opção</option>
              {normalizedOptions.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );
      }
      // Senão, usar input de texto
      return (
        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      );
    }
    // Modo exibição: se houver opções do formulário, mostrar o LABEL da opção
    // correspondente ao valor salvo (ex.: "demais_colaboradores" -> "Colaboradores"),
    // refletindo a pergunta configurada no formulário do cliente, não o valor do banco.
    let displayValue = value;
    if (value && options && options.length > 0) {
      const match = options.find((opt) => {
        if (typeof opt === 'object' && opt !== null) {
          return String(opt.value ?? '').trim() === String(value).trim() ||
                 String(opt.label ?? '').trim() === String(value).trim();
        }
        return String(opt).trim() === String(value).trim();
      });
      if (match) {
        displayValue = (typeof match === 'object' && match !== null)
          ? String(match.label ?? match.value ?? value)
          : String(match);
      }
    }
    return (
      <div className="mb-2">
        <span className="text-sm font-medium text-gray-700">{label}:</span>
        <span className="ml-2 text-sm text-gray-900">{displayValue || '-'}</span>
      </div>
    );
  };

  // O formulário deste cliente divide a visualização (sobre_quem, unidade...)?
  // Só nesse caso o campo é obrigatório para aprovar (espelha o backend).
  const formAsksSobreQuem = (complaint) => Boolean(findRoutingField(complaint.form_fields));

  // Pergunta que divide a visualização, para rotular o campo e o aviso.
  const routingLabel = (complaint) => {
    const field = findRoutingField(complaint.form_fields);
    return field && field !== findField(complaint.form_fields, 'sobre_quem') && field.label ? field.label : 'Sobre Quem';
  };

  // Resposta já editada no campo de direcionamento acima: a do campo que divide
  // a visualização, ou o sobre_quem (e aliases) quando é ele ou não há divisão.
  const routingKeys = (complaint) => {
    const field = findRoutingField(complaint.form_fields);
    return field && field !== findField(complaint.form_fields, 'sobre_quem')
      ? [field.name]
      : ['sobre_quem', 'sobreQuem', 'sobre_quem_denuncia'];
  };

  // Opções do campo que divide a visualização conforme o formulário. Só caímos
  // nos públicos padrão quando o formulário NÃO divide — se o campo existe como
  // texto livre, preservamos o input de texto (comportamento anterior).
  const getSobreQuemOptions = (complaint) => {
    const field = findRoutingField(complaint.form_fields);
    if (!field) return PUBLICOS_PADRAO;
    return Array.isArray(field.options) && field.options.length > 0 ? field.options : null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando denúncias pendentes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rf-screen">
      <div style={{ maxWidth: 1180, width: '100%' }}>
        {/* Cabeçalho da tela: identidade fora do cartão. Antes o título ficava
            DENTRO de um card branco junto com os filtros — o cartão sugeria
            "conteúdo", quando é só o topo da página. */}
        <div className="rf-screen__head" style={{ marginBottom: 16 }}>
          <div>
            <h1 className="rf-screen__title">Aprovação de denúncias</h1>
            <p className="rf-screen__sub">
              {filteredComplaints.length} pendente{filteredComplaints.length === 1 ? '' : 's'} · triagem automática por IA
            </p>
          </div>
          <div className="rf-screen__actions">
            <button onClick={loadPendingApprovals} className="rf-btn rf-btn--ghost">
              Atualizar
            </button>
          </div>
        </div>

        {/* Filtros numa linha só, acima da lista que eles afetam. */}
        <div className="rf-card" style={{ padding: 16, marginBottom: 16 }}>
          <div className="rf-toolbar">
            <div className="rf-toolbar__grow">
              <div className="rf-search">
                <Search size={17} className="rf-search__icon" aria-hidden="true" />
                <input
                  type="text"
                  placeholder="Buscar por token, pessoa citada ou conteúdo"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="rf-input"
                  aria-label="Buscar denúncias"
                />
              </div>
            </div>
            <select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              className="rf-select"
              style={{ width: 'auto' }}
              aria-label="Filtrar por cliente"
            >
              <option value="all">Todos os clientes</option>
              {uniqueClients.map((client) => (
                <option key={client} value={client}>{client}</option>
              ))}
            </select>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="rf-select"
              style={{ width: 'auto' }}
              aria-label="Filtrar por severidade"
            >
              <option value="all">Toda severidade</option>
              <option value="crít">Crítico</option>
              <option value="alto">Alto</option>
              <option value="méd">Médio</option>
              <option value="baixo">Baixo</option>
            </select>
          </div>
        </div>

        {/* Lista de Denúncias */}
        {filteredComplaints.length === 0 ? (
          <div className="rf-card">
            <div className="rf-empty">
              Nenhuma denúncia aguardando aprovação neste recorte.
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredComplaints.map(complaint => {
              const isEditing = editingId === complaint.id;
              const responses = complaint.responses || {};
              
              return (
                <div key={complaint.id} className="rf-card" style={{ padding: 20 }}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="rf-case__id" style={{ fontSize: '.95rem' }}>
                          {displayProtocolTag(complaint)}
                        </span>
                        <span className="rf-badge" style={{ background: 'rgba(250,178,25,.18)', color: '#9a6700' }}>
                          Pendente
                        </span>
                        {complaint.escopo ? (
                          <span className={`rounded border px-2 py-1 text-xs font-medium ${getEscopoStyle(complaint.escopo)}`}>
                            {ESCOPO_LABELS[complaint.escopo] || complaint.escopo}
                          </span>
                        ) : null}
                        {complaint.ai_analise?.denuncia_estruturada?.severidade ? (
                          <span className={`rounded border px-2 py-1 text-xs font-medium ${getSeverityStyle(complaint.ai_analise.denuncia_estruturada.severidade)}`}>
                            IA: {complaint.ai_analise.denuncia_estruturada.severidade}
                          </span>
                        ) : complaint.ai_phase === 'investigation_pending' ? (
                          <span className="flex items-center gap-1 rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Triando...
                          </span>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          Token: {complaint.tracking_token}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(complaint.submitted_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Building className="w-4 h-4" />
                          {complaint.client_name || complaint.client_slug}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => saveChanges(complaint.id)}
                            disabled={saving}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                          >
                            <Save className="w-4 h-4" />
                            {saving ? 'Salvando...' : 'Salvar'}
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors flex items-center gap-2"
                          >
                            <X className="w-4 h-4" />
                            Cancelar
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEditing(complaint)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                          >
                            <Edit className="w-4 h-4" />
                            Editar
                          </button>
                          {/* sobre_quem só bloqueia a aprovação quando o formulário do
                              cliente realmente pergunta isso — formulários sem o campo
                              gravam NULL e ficavam impossíveis de aprovar. */}
                          <button
                            onClick={() => approveComplaint(complaint.id)}
                            disabled={approving === complaint.id || (formAsksSobreQuem(complaint) && !complaint.sobre_quem)}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            <CheckCircle className="w-4 h-4" />
                            {approving === complaint.id ? 'Aprovando...' : 'Aprovar'}
                          </button>
                          {/* Botão de delete - apenas para admin/super_admin */}
                          {(user.role === 'admin' || user.role === 'super_admin') && (
                            <button
                              onClick={() => handleDeleteClick(complaint.id)}
                              disabled={deleting === complaint.id}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              {deleting === complaint.id ? 'Deletando...' : 'Deletar'}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Dados da Denúncia</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Campo sobre_quem */}
                      {(() => {
                        const sobreQuemOptions = getSobreQuemOptions(complaint);
                        const hasSobreQuemField = formAsksSobreQuem(complaint);
                        // Quando está editando, usar editedData.sobre_quem se existir (mesmo que seja string vazia)
                        // Caso contrário, usar o valor original da denúncia
                        const currentSobreQuem = isEditing
                          ? (editedData.sobre_quem !== undefined ? editedData.sobre_quem : complaint.sobre_quem)
                          : complaint.sobre_quem;
                        
                        console.log('📊 Renderizando campo sobre_quem:', {
                          isEditing,
                          editedData_sobre_quem: editedData.sobre_quem,
                          complaint_sobre_quem: complaint.sobre_quem,
                          currentSobreQuem,
                          hasOptions: !!sobreQuemOptions,
                          optionsCount: sobreQuemOptions?.length
                        });
                        
                        return (
                          <div>
                            {renderField(
                              routingLabel(complaint),
                              currentSobreQuem,
                              isEditing,
                              (value) => handleFieldChange('sobre_quem', value),
                              sobreQuemOptions
                            )}
                            {!hasSobreQuemField && (
                              <p className="text-xs text-gray-500 mt-1">
                                Este formulário não divide a visualização entre os admins — o
                                preenchimento é opcional e não impede a aprovação. Use Editar se
                                quiser direcionar a visibilidade por público.
                              </p>
                            )}
                          </div>
                        );
                      })()}

                      {/* Formulário */}
                      <div className="mb-2">
                        <span className="text-sm font-medium text-gray-700">Formulário:</span>
                        <span className="ml-2 text-sm text-gray-900">{complaint.form_title || '-'}</span>
                      </div>

                      {/* Usuário */}
                      <div className="mb-2">
                        <span className="text-sm font-medium text-gray-700">Usuário:</span>
                        <span className="ml-2 text-sm text-gray-900">
                          {complaint.user_full_name || complaint.username || 'Anônimo'}
                        </span>
                      </div>
                    </div>

                    {/* Campos do formulário (responses) */}
                    {Object.keys(responses).length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <h4 className="text-sm font-semibold text-gray-900 mb-3">Respostas do Formulário</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {Object.entries(responses).map(([key, value]) => {
                            if (isEditing && routingKeys(complaint).includes(key)) {
                              return null; // Já renderizado acima
                            }
                            const responseValue = isEditing
                              ? (editedData.responses?.[key] ?? value)
                              : value;
                            return (
                              <div key={key} className="mb-2">
                                {isEditing && key !== 'identificacao' && key !== 'anonymous_username' && key !== 'anonymous_password' ? (
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      {getQuestionLabel(complaint.form_fields, key)}:
                                    </label>
                                    <input
                                      type="text"
                                      value={responseValue || ''}
                                      onChange={(e) => handleResponseFieldChange(key, e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                ) : (
                                  <div>
                                    <span className="text-sm font-medium text-gray-700">
                                      {getQuestionLabel(complaint.form_fields, key)}:
                                    </span>
                                    <span className="ml-2 text-sm text-gray-900">
                                      {(() => {
                                        // Mostra o texto que o denunciante escolheu, não o valor
                                        // interno gravado no banco (ex.: "Comitê de Ética", não "diretores").
                                        const shown = resolveAnswer(complaint.form_fields, key, value);
                                        return typeof shown === 'object' ? JSON.stringify(shown) : String(shown || '-');
                                      })()}
                                    </span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <CaseAnalysisSection
                      complaint={complaint}
                      isEditing={isEditing}
                      data={editedData}
                      onFieldChange={handleFieldChange}
                      onNormaChange={handleNormaChange}
                      onNormaAdd={handleNormaAdd}
                      onNormaRemove={handleNormaRemove}
                    />

                    <TriageInsight
                      complaint={complaint}
                      onReanalyze={handleReanalyze}
                      reanalyzing={reanalyzing}
                    />

                    {/* Alerta se sobre_quem não estiver preenchido (só quando o formulário pergunta) */}
                    {formAsksSobreQuem(complaint) && !complaint.sobre_quem && (
                      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-yellow-800">
                            Campo "{routingLabel(complaint)}" não preenchido
                          </p>
                          <p className="text-sm text-yellow-700 mt-1">
                            É necessário preencher o campo "{routingLabel(complaint)}" antes de aprovar a denúncia.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal de confirmação de delete */}
      {deleteModal.open && (
        <div className="rf-modal">
          <div className="rf-modal__panel rf-modal__panel--narrow">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Confirmar exclusão</h3>
                  <p className="text-sm text-gray-600">Esta ação não pode ser desfeita</p>
                </div>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-700 mb-2">
                  Para confirmar a exclusão, digite exatamente:
                </p>
                <p className="text-sm font-mono bg-gray-100 p-2 rounded mb-4 text-center">
                  tenho certeza que vou deletar a denúncia
                </p>
                <input
                  type="text"
                  value={deleteModal.confirmationText}
                  onChange={(e) => setDeleteModal({ ...deleteModal, confirmationText: e.target.value })}
                  placeholder="Digite o texto de confirmação"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleDeleteConfirm();
                    }
                  }}
                />
              </div>
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleDeleteCancel}
                  disabled={deleting === deleteModal.complaintId}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={
                    deleting === deleteModal.complaintId ||
                    deleteModal.confirmationText.toLowerCase().trim() !== 'tenho certeza que vou deletar a denúncia'
                  }
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {deleting === deleteModal.complaintId ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Deletando...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Deletar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplaintApproval;

